/**
 * The one way the browser talks to the API.
 *
 * Every mutating request carries the session's CSRF token in `x-praxadon-csrf` — the
 * header a page on another host can neither read nor send (plan.md §5) — and the token
 * is refreshed from any response that carries one. Errors arrive as the API's
 * `{ error: { code, message, … } }` and become an `ApiError` whose message is already
 * written for a beginner, so UI code can show `error.message` as it is.
 */

export interface ApiErrorBody {
  code: string;
  message: string;
  fields?: Record<string, string>;
  details?: Record<string, unknown>;
  retryAfter?: number;
  requestId?: string;
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly fields: Record<string, string>;
  readonly details: Record<string, unknown>;
  readonly retryAfter: number | undefined;
  readonly requestId: string | undefined;

  constructor(status: number, body: ApiErrorBody) {
    super(body.message);
    this.name = 'ApiError';
    this.status = status;
    this.code = body.code;
    this.fields = body.fields ?? {};
    this.details = body.details ?? {};
    this.retryAfter = body.retryAfter;
    this.requestId = body.requestId;
  }
}

/** Turn anything thrown into something with a message a student can read. */
export function describeError(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  return 'Something went wrong. Please try again.';
}

let csrfToken: string | null = null;

export function setCsrfToken(token: string | null): void {
  csrfToken = token;
}

export function getCsrfToken(): string | null {
  return csrfToken;
}

// ------------------------------------------------------------------ lost sessions

const sessionLostListeners = new Set<() => void>();

/**
 * Hear about a session ending underneath the page: it expired, or was ended elsewhere
 * (log out everywhere, a password change, a ban). Told once per session.
 */
export function onSessionLost(listener: () => void): () => void {
  sessionLostListeners.add(listener);
  return () => {
    sessionLostListeners.delete(listener);
  };
}

const SESSION_LOST_CODES = new Set(['unauthenticated', 'session_expired']);

/** A signed-in page (it holds a CSRF token) was just told it is not signed in. */
function noticeSessionLost(error: ApiError): void {
  if (csrfToken === null || !SESSION_LOST_CODES.has(error.code)) return;
  csrfToken = null;
  for (const listener of sessionLostListeners) listener();
}

/**
 * The token this page holds belongs to a session that was replaced — the student
 * signed in again in another tab, so the shared cookie now names a new session. Fetch
 * that session's token. False if there is no session any more.
 */
async function refreshCsrfToken(): Promise<boolean> {
  const response = await send('GET', '/api/auth/session', {});
  if (!response.ok) return false;
  const session = (await response.json()) as { authenticated?: boolean; csrfToken?: unknown };
  if (session.authenticated && typeof session.csrfToken === 'string') {
    csrfToken = session.csrfToken;
    return true;
  }
  return false;
}

type Query = Record<string, string | number | boolean | undefined | null>;

export interface RequestOptions {
  body?: unknown;
  query?: Query;
  signal?: AbortSignal;
  /** Send a FormData body (uploads). */
  form?: FormData;
}

export function buildUrl(path: string, query?: Query): string {
  if (!query) return path;
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined && value !== null) params.set(key, String(value));
  }
  const text = params.toString();
  return text ? `${path}?${text}` : path;
}

const NETWORK_ERROR: ApiErrorBody = {
  code: 'network_error',
  message:
    "We can't reach the server right now. Check your connection — your work is kept safe in this browser meanwhile.",
};

async function send(method: string, path: string, options: RequestOptions): Promise<Response> {
  const headers: Record<string, string> = { accept: 'application/json' };
  if (method !== 'GET' && method !== 'HEAD' && csrfToken) headers['x-praxadon-csrf'] = csrfToken;

  let body: BodyInit | undefined;
  if (options.form) body = options.form;
  else if (options.body !== undefined) {
    headers['content-type'] = 'application/json';
    body = JSON.stringify(options.body);
  }

  try {
    return await fetch(buildUrl(path, options.query), {
      method,
      headers,
      credentials: 'same-origin',
      ...(body === undefined ? {} : { body }),
      ...(options.signal ? { signal: options.signal } : {}),
    });
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') throw error;
    throw new ApiError(0, NETWORK_ERROR);
  }
}

async function readError(response: Response): Promise<ApiError> {
  try {
    const parsed = (await response.json()) as { error?: ApiErrorBody };
    if (parsed.error?.code) return new ApiError(response.status, parsed.error);
  } catch {
    /* not JSON: a proxy error page, say */
  }
  return new ApiError(response.status, {
    code: response.status >= 500 ? 'internal_error' : 'bad_request',
    message:
      response.status >= 500
        ? 'Something went wrong on our side. Please try again in a moment.'
        : "Something about that request wasn't right. Please try again.",
  });
}

/**
 * Send a request, retrying once with a fresh token if the session behind the page's CSRF
 * token was replaced, and turn a refusal into an `ApiError` — telling the page if the
 * session is gone.
 */
async function checked(method: string, path: string, options: RequestOptions): Promise<Response> {
  let response = await send(method, path, options);
  if (response.status === 403 && method !== 'GET' && csrfToken !== null) {
    const refused = await readError(response.clone());
    if (refused.code === 'csrf_failed' && (await refreshCsrfToken())) {
      response = await send(method, path, options);
    }
  }
  if (!response.ok) {
    const error = await readError(response);
    if (response.status === 401) noticeSessionLost(error);
    throw error;
  }
  return response;
}

/** JSON request. Resolves to the parsed body; rejects with an ApiError. */
export async function api<T>(
  method: string,
  path: string,
  options: RequestOptions = {},
): Promise<T> {
  const response = await checked(method, path, options);
  if (response.status === 204) return undefined as T;
  const parsed = (await response.json()) as T;
  const token = (parsed as { csrfToken?: unknown } | null)?.csrfToken;
  if (typeof token === 'string') setCsrfToken(token);
  return parsed;
}

/**
 * A request answered as a stream of events (an AI answer): refusals come back as
 * `ApiError`s exactly as from `api`, and a stream that has started is handed back for
 * `readEvents` (sse.ts) to read.
 */
export async function apiStream(path: string, options: RequestOptions = {}): Promise<Response> {
  return checked('POST', path, options);
}

/** A request whose body is not JSON (a file's bytes); returns the raw response. */
export async function apiRaw(path: string, options: RequestOptions = {}): Promise<Response> {
  const response = await send('GET', path, options);
  if (!response.ok) throw await readError(response);
  return response;
}
